import { fn } from '@ember/helper';
import { guidFor } from '@ember/object/internals';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pluralize } from 'ember-inflector';
import EmberPopover from 'ember-tooltips/components/ember-popover';
import LineClamp from 'garaje/components/line-clamp';
import UiPill from 'garaje/components/ui-pill';
import type Invite from 'garaje/models/invite';
import { reads } from 'macro-decorators';

interface InvitesTableInviteListNameCellSignature {
  Args: {
    delay?: number;
    lineHeight?: number;
    lines?: number;
    model?: Invite;
    popperContainer?: string;
    route?: string;
    side?: string;
    text?: string;
    tooltipClass?: string;
  };
  Blocks: {
    default: [data: string];
  };
  Element: HTMLTableCellElement;
}

export default class InvitesTableInviteListNameCell extends Component<InvitesTableInviteListNameCellSignature> {
  @tracked textWasTruncated: boolean = false;

  @reads('args.delay', 400) delay!: number;
  @reads('args.lineHeight', 1) lineHeight!: number;
  @reads('args.lines', 1) lines!: number;
  @reads('args.popperContainer', '.ui__page') popperContainer!: string;
  @reads('args.route', 'visitors.invites.show') route!: string;
  @reads('args.side', 'top') side!: string;
  @reads('args.tooltipClass', 'text-sm font-bold break-word max-w-sm z-50') tooltipClass!: string;

  contentElementId = guidFor(this);

  get data(): string {
    const { text, model } = this.args;

    return text || model?.fullName || '';
  }

  <template>
    {{#if (has-block)}}
      {{yield this.data}}
    {{else}}
      <th ...attributes data-test-invites-list-name-cell>
        <LinkTo
          @route={{this.route}}
          @model={{@model.id}}
          class="flex items-start text-base font-bold no-underline text-carbon-70 hover:text-red-50 trans-color"
          data-test-invite-link-to={{@model.id}}
        >
          <LineClamp
            @text={{this.data}}
            @lines={{this.lines}}
            @lineHeight={{this.lineHeight}}
            @handleTruncate={{fn (mut this.textWasTruncated)}}
            aria-hidden="true"
            data-test-invites-list-name-cell-content
          />
          {{#if @model.hasAdditionalGuests}}
            <div aria-hidden="true">
              <UiPill class="whitespace-nowrap ml-1 mt-[-0.5em]" @fill="grayDark" data-test-additional-guests>
                +{{@model.additionalGuests}}
                {{pluralize @model.additionalGuests "visitor" withoutCount=true}}
              </UiPill>
            </div>
          {{/if}}
          {{#if this.textWasTruncated}}
            <EmberPopover
              @tooltipClass={{this.tooltipClass}}
              @popperContainer={{this.popperContainer}}
              @delay={{this.delay}}
              @text={{this.data}}
              data-test-invites-list-name-cell-tooltip
            />
          {{/if}}

          <div class="sr-only">
            {{! Full visitor name available to screen reader }}
            Visitor:
            <span data-test-invite-full-name>{{this.data}}</span>
            {{#if @model.hasAdditionalGuests}}
              and
              {{@model.additionalGuests}}
              {{pluralize @model.additionalGuests "visitor" withoutCount=true}}
            {{/if}}.
          </div>
        </LinkTo>
      </th>
    {{/if}}
  </template>
}
